import { demoLogin, startConversation } from './api'
import {
  CALL_CHANNEL,
  CONNECT_WATCHDOG_MS,
  ENDED_BADGE_MS,
  getVoiceDemoConfig,
  HIDDEN_GRACE_MS,
  MAX_CALL_MS,
  MIC_GAIN,
  MIC_OPTIONS,
  MIC_OPTIONS_IOS,
  RATE_LIMIT_COOLDOWN_MS,
  SLOW_CONNECT_MS,
  DEMO_USER_ID
} from './config'
import { startFailureMessage, tokenExpired, upsertTranscription } from './domain'
import {
  getUiAudioContext,
  playCallEndSound,
  playMuteSound,
  playTapSound,
  startRinging,
  stopRinging,
  stopRingingAndPlayConnect,
  unlockAudio
} from './audioUtils'
import { showToast } from './toast'

const isIOS = () =>
  /iPad|iPhone|iPod|CriOS|FxiOS/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const claimIosCallSession = () => {
  try {
    const session = navigator.audioSession
    if (session) session.type = 'play-and-record'
  } catch {
    /* Audio Session API missing or rejected */
  }
}

// Node 20+ ships BroadcastChannel; creating one at module load keeps `nuxt generate` alive.
const callChannel =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(CALL_CHANNEL)
    : null

const AudioCtx = () => window.AudioContext || window.webkitAudioContext

let sharedCtx = null
let analyserCtx = null
const audioContext = () => {
  const Ctor = AudioCtx()
  sharedCtx ??= new Ctor()
  void sharedCtx.resume()
  return sharedCtx
}

const analyserAudioContext = () => {
  const Ctor = AudioCtx()
  analyserCtx ??= new Ctor()
  if (analyserCtx.state !== 'running' && analyserCtx.state !== 'closed') {
    void analyserCtx.resume().catch(() => undefined)
  }
  return analyserCtx
}

const audioContexts = () => [sharedCtx, analyserCtx, getUiAudioContext()].filter(Boolean)

export function resumeOrbAudio() {
  audioContexts().forEach((ctx) => {
    if (ctx.state !== 'running' && ctx.state !== 'closed') void ctx.resume().catch(() => undefined)
  })
}

const makeAnalyser = (ctx) => {
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = 0.8
  return analyser
}

const tapTrack = (mediaStreamTrack) => {
  const ctx = analyserAudioContext()
  // Clone so the analyser never consumes the published or playback track.
  // Chrome mutes a getUserMedia track that is both sent over WebRTC and
  // wired into Web Audio; Safari can steal HTMLAudioElement playback too.
  const clone = mediaStreamTrack.clone()
  const source = ctx.createMediaStreamSource(new MediaStream([clone]))
  const analyser = makeAnalyser(ctx)
  source.connect(analyser)
  return {
    analyser,
    dispose: () => {
      source.disconnect()
      clone.stop()
    }
  }
}

const hideAttachedAudio = (el) => {
  el.setAttribute('aria-hidden', 'true')
  Object.assign(el.style, {
    position: 'fixed',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    left: '0',
    top: '0'
  })
}

const micCaptureOptions = () =>
  isIOS() ? MIC_OPTIONS_IOS : { ...MIC_OPTIONS, deviceId: 'default' }

const createGainProcessor = (gainValue) => {
  const state = {
    source: null,
    gainNode: null,
    destination: null,
    processedTrack: null
  }

  const cleanup = () => {
    state.processedTrack?.stop()
    state.source?.disconnect()
    state.gainNode?.disconnect()
    state.destination?.disconnect()
    state.processedTrack = null
    state.source = null
    state.gainNode = null
    state.destination = null
  }

  const processor = {
    name: 'GainNodeProcessor',
    processedTrack: undefined,
    async init({ track, audioContext: ctx }) {
      if (ctx.state === 'closed') throw new Error('AudioContext is closed')
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
      state.source = ctx.createMediaStreamSource(new MediaStream([track]))
      state.gainNode = ctx.createGain()
      state.destination = ctx.createMediaStreamDestination()
      state.gainNode.gain.value = gainValue
      state.source.connect(state.gainNode)
      state.gainNode.connect(state.destination)
      const [processed] = state.destination.stream.getAudioTracks()
      if (processed) {
        state.processedTrack = processed
        processor.processedTrack = processed
      }
    },
    async restart(opts) {
      const current = state.gainNode?.gain.value ?? gainValue
      cleanup()
      await processor.init(opts)
      if (state.gainNode) state.gainNode.gain.value = current
    },
    async destroy() {
      cleanup()
      processor.processedTrack = undefined
    }
  }

  return processor
}

function visitorId(userIdKey) {
  try {
    localStorage.setItem(userIdKey, DEMO_USER_ID)
  } catch {
    /* ignore quota / private mode */
  }
  return DEMO_USER_ID
}

function loadStoredAuth(agentId, authTokenKey, authAgentKey) {
  try {
    const token = localStorage.getItem(authTokenKey)
    const storedAgent = localStorage.getItem(authAgentKey)
    if (!token || storedAgent !== agentId || tokenExpired(token)) return null
    return token
  } catch {
    return null
  }
}

function persistAuth(agentId, token, authTokenKey, authAgentKey) {
  try {
    localStorage.setItem(authTokenKey, token)
    localStorage.setItem(authAgentKey, agentId)
  } catch {
    /* ignore quota / private mode */
  }
}

function clearAuth(authTokenKey, authAgentKey) {
  try {
    localStorage.removeItem(authTokenKey)
    localStorage.removeItem(authAgentKey)
  } catch {
    /* ignore */
  }
}

export function useVoiceSession({ agentId, scenario, copy, locale = 'ko' }) {
  const config = getVoiceDemoConfig(unref(locale))

  const sequence = ref('idle')
  const starting = ref(false)
  const coolingDown = ref(false)
  const loginStatus = ref('pending')
  const token = ref(null)
  const micMuted = ref(false)
  const micStreamAllowed = ref(false)
  const micPermission = ref(null)
  const isSpeaking = ref(false)
  const reconnecting = ref(false)
  const elapsedSeconds = ref(0)
  const log = ref([])
  const outputAnalyser = ref(null)
  const inputAnalyser = ref(null)

  const roomRef = shallowRef(null)
  const livekitRef = shallowRef(null)
  const pendingStart = ref(false)
  const sessionNonce = ref(0)
  const manualEnd = ref(false)
  const attachedEls = []
  const tapDisposers = []
  let gainProcessor = null
  let primedMicStream = null
  let primedMicPromise = null

  let watchdogTimer = null
  let slowTimer = null
  let maxCallTimer = null
  let hiddenTimer = null
  let hiddenAt = 0
  let cooldownTimer = null
  let elapsedTimer = null
  let endedTimer = null
  let connectStartedAt = 0
  let muteBusy = false

  const inCall = computed(() => sequence.value === 'connecting' || sequence.value === 'connected')
  const connected = computed(() => sequence.value === 'connected')
  const ended = computed(() => sequence.value === 'disconnected')
  const micBlocked = computed(() => connected.value && !micStreamAllowed.value)
  const micDenied = computed(() => micPermission.value === 'denied')
  const loginLocked = computed(() => loginStatus.value !== 'ok')
  const visibleLog = computed(() => log.value.filter((m) => !(m.role === 'user' && m.isFinal === false)))

  const clearTimers = () => {
    ;[watchdogTimer, slowTimer, maxCallTimer, hiddenTimer, cooldownTimer, elapsedTimer, endedTimer].forEach(
      (id) => id && clearTimeout(id)
    )
    watchdogTimer = slowTimer = maxCallTimer = hiddenTimer = cooldownTimer = elapsedTimer = endedTimer = null
  }

  const detachAudio = () => {
    attachedEls.splice(0).forEach((el) => {
      try {
        el.srcObject = null
        el.remove()
      } catch {
        /* ignore */
      }
    })
    tapDisposers.splice(0).forEach((dispose) => {
      try {
        dispose()
      } catch {
        /* ignore */
      }
    })
    if (gainProcessor) {
      void gainProcessor.destroy().catch(() => undefined)
      gainProcessor = null
    }
    primedMicStream?.getTracks().forEach((track) => track.stop())
    primedMicStream = null
    primedMicPromise = null
    outputAnalyser.value = null
    inputAnalyser.value = null
  }

  const setIdleSoon = (nonce) => {
    endedTimer = setTimeout(() => {
      if (nonce !== sessionNonce.value) return
      sequence.value = 'idle'
      manualEnd.value = false
      elapsedSeconds.value = 0
    }, ENDED_BADGE_MS)
  }

  const probeMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: micCaptureOptions() })
      stream.getTracks().forEach((track) => track.stop())
      micStreamAllowed.value = true
      return true
    } catch {
      micStreamAllowed.value = false
      return false
    }
  }

  const requestMic = async () => {
    const allowed = await probeMic()
    if (!allowed) {
      showToast(copy.value.micAllowToast)
      return
    }
    const room = roomRef.value
    if (room?.state === 'connected' && !micMuted.value) {
      try {
        await enableMicrophone(room)
      } catch {
        showToast(copy.value.micAllowToast)
      }
    }
  }

  const publishPrimedMic = async (room, stream) => {
    const mediaTrack = stream.getAudioTracks()[0]
    if (!mediaTrack) return
    const { Track } = await loadLiveKit()
    await room.localParticipant.publishTrack(mediaTrack, {
      source: Track.Source.Microphone
    })
    micStreamAllowed.value = true
  }

  const enableMicrophone = (room) => {
    if (isIOS()) claimIosCallSession()
    return room.localParticipant.setMicrophoneEnabled(true, micCaptureOptions())
  }

  const disableMicrophone = (room) => room.localParticipant.setMicrophoneEnabled(false)

  const applyMute = async (muted) => {
    const room = roomRef.value
    if (!room || room.state !== 'connected' || !livekitRef.value) return
    try {
      if (isIOS()) {
        if (muted) {
          await disableMicrophone(room)
        } else {
          await disableMicrophone(room)
          await new Promise((resolve) => setTimeout(resolve, 100))
          await enableMicrophone(room)
        }
      } else if (muted) {
        await disableMicrophone(room)
      } else {
        await enableMicrophone(room)
      }
    } catch {
      micStreamAllowed.value = false
    }
  }

  const tapPublishedMic = (audioTrack) => {
    const sourceTrack = audioTrack.mediaStreamTrack
    if (!sourceTrack) return
    const tap = tapTrack(sourceTrack)
    tapDisposers.push(tap.dispose)
    inputAnalyser.value = tap.analyser
  }

  const applyGainProcessor = async (audioTrack) => {
    // Safari publishes silence if the live mic is routed through Web Audio.
    if (isIOS()) {
      tapPublishedMic(audioTrack)
      return
    }

    const ctx = audioContext()
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
    audioTrack.setAudioContext?.(ctx)

    try {
      await audioTrack.stopProcessor()
    } catch {
      /* no existing processor */
    }
    if (gainProcessor) {
      await gainProcessor.destroy().catch(() => undefined)
      gainProcessor = null
    }

    const processor = createGainProcessor(MIC_GAIN)
    gainProcessor = processor
    try {
      await audioTrack.setProcessor(processor)
      tapPublishedMic(audioTrack)
    } catch {
      gainProcessor = null
    }
  }

  const loadLiveKit = async () => {
    if (livekitRef.value) return livekitRef.value
    livekitRef.value = markRaw(await import('livekit-client'))
    return livekitRef.value
  }

  const ensureRoom = async () => {
    if (roomRef.value) return roomRef.value
    const { Room, RoomEvent, Track } = await loadLiveKit()
    const room = markRaw(
      new Room({
        webAudioMix: false,
        audioCaptureDefaults: micCaptureOptions()
      })
    )
    roomRef.value = room

    try {
      const wsUrl = localStorage.getItem(config.livekitWsKey)
      if (wsUrl) void room.prepareConnection(wsUrl)
    } catch {
      /* ignore */
    }

    const markConnected = () => {
      if (sequence.value !== 'connecting') return
      void stopRingingAndPlayConnect()
      sequence.value = 'connected'
      connectStartedAt = Date.now()
      elapsedSeconds.value = 0
      elapsedTimer = setInterval(() => {
        elapsedSeconds.value = Math.floor((Date.now() - connectStartedAt) / 1000)
      }, 1000)
      if (watchdogTimer) clearTimeout(watchdogTimer)
      if (slowTimer) clearTimeout(slowTimer)
      const nonce = sessionNonce.value
      maxCallTimer = setTimeout(() => {
        // Room is reused across calls, so this handler re-arms the timer every
        // connect. Without the nonce check a timer left over from an earlier
        // call ends the current one, minutes before its own limit.
        if (nonce !== sessionNonce.value) return
        showToast(copy.value.maxDuration)
        void endCall()
      }, MAX_CALL_MS)
    }

    room.on(RoomEvent.Connected, () => {
      markConnected()
      void room.startAudio().catch(() => {
        window.addEventListener('pointerdown', () => void room.startAudio().catch(() => undefined), { once: true })
      })
    })

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio || !track.mediaStreamTrack) return
      const el = track.attach()
      el.autoplay = true
      el.playsInline = true
      el.setAttribute('playsinline', '')
      hideAttachedAudio(el)
      document.body.appendChild(el)
      attachedEls.push(el)
      void el.play().catch(() => {
        window.addEventListener('pointerdown', () => void el.play().catch(() => undefined), { once: true })
      })
      const tap = tapTrack(track.mediaStreamTrack)
      tapDisposers.push(tap.dispose)
      outputAnalyser.value = tap.analyser
    })

    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) return
      track.detach().forEach((el) => {
        el.remove()
      })
      outputAnalyser.value = null
    })

    room.on(RoomEvent.LocalTrackPublished, (publication) => {
      const localTrack = publication.track
      if (publication.source !== Track.Source.Microphone || !localTrack) return
      micStreamAllowed.value = true
      void applyGainProcessor(localTrack)
    })

    room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
      if (publication.source !== Track.Source.Microphone) return
      inputAnalyser.value = null
      if (gainProcessor) {
        void publication.track?.stopProcessor?.().catch(() => undefined)
        void gainProcessor.destroy().catch(() => undefined)
        gainProcessor = null
      }
    })

    room.on(RoomEvent.MediaDevicesError, () => {
      micStreamAllowed.value = false
      showToast(copy.value.micAllowToast)
    })

    room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
      segments.forEach((segment) => {
        if (!segment.text) return
        log.value = upsertTranscription(log.value, {
          id: segment.id,
          role: participant?.isLocal ? 'user' : 'agent',
          text: segment.text,
          isFinal: segment.final ?? false
        })
      })
    })

    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      isSpeaking.value = speakers.some((speaker) => speaker.identity !== room.localParticipant.identity)
    })

    room.on(RoomEvent.Reconnecting, () => {
      reconnecting.value = true
      showToast(copy.value.reconnecting)
    })
    room.on(RoomEvent.Reconnected, () => {
      reconnecting.value = false
    })
    room.on(RoomEvent.Disconnected, () => {
      reconnecting.value = false
      detachAudio()
      log.value = []
      if (manualEnd.value) return
      // An agent hangup or a dropped connection never runs endCall(), so the
      // max-duration and away-from-page timers stay armed and fire against
      // whatever call happens to be running by then.
      clearTimers()
      stopRinging()
      playCallEndSound()
      sequence.value = 'disconnected'
      micMuted.value = false
      setIdleSoon(sessionNonce.value)
    })

    return room
  }

  const issueToken = async (nextAgentId) => {
    loginStatus.value = 'pending'
    try {
      const cached = loadStoredAuth(nextAgentId, config.authTokenKey, config.authAgentKey)
      if (cached) {
        token.value = cached
        loginStatus.value = 'ok'
        if (pendingStart.value) {
          pendingStart.value = false
          void beginCall()
        }
        return
      }
      const data = await demoLogin({
        apiOrigin: config.apiOrigin,
        appId: config.appId,
        agentId: nextAgentId
      })
      token.value = data.token
      persistAuth(nextAgentId, data.token, config.authTokenKey, config.authAgentKey)
      loginStatus.value = 'ok'
      if (pendingStart.value) {
        pendingStart.value = false
        void beginCall()
      }
    } catch {
      token.value = null
      clearAuth(config.authTokenKey, config.authAgentKey)
      loginStatus.value = 'failed'
    }
  }

  const beginCall = async () => {
    if (sequence.value !== 'idle' || starting.value || coolingDown.value || config.pbBuilder) return
    starting.value = true
    const nonce = ++sessionNonce.value
    manualEnd.value = false
    log.value = []

    try {
      if (!token.value || tokenExpired(token.value)) {
        pendingStart.value = true
        clearAuth(config.authTokenKey, config.authAgentKey)
        token.value = null
        await issueToken(agentId.value)
        if (!token.value) throw Object.assign(new Error('login'), { status: 401 })
      }

      const data = await startConversation({
        apiOrigin: config.apiOrigin,
        agentId: agentId.value,
        token: token.value,
        userId: visitorId(config.userIdKey)
      })

      if (data.livekit?.ws_url) {
        try {
          localStorage.setItem(config.livekitWsKey, data.livekit.ws_url)
        } catch {
          /* ignore */
        }
      }

      sequence.value = 'connecting'
      startRinging()
      watchdogTimer = setTimeout(() => {
        if (nonce !== sessionNonce.value) return
        if (sequence.value !== 'connecting') return
        showToast(copy.value.genericError)
        void endCall()
      }, CONNECT_WATCHDOG_MS)
      slowTimer = setTimeout(() => {
        if (sequence.value === 'connecting') showToast(copy.value.slowConnect)
      }, SLOW_CONNECT_MS)

      const room = await ensureRoom()
      await room.connect(data.livekit.ws_url, data.livekit.access_token)
      try {
        await room.startAudio()
      } catch {
        window.addEventListener('pointerdown', () => void room.startAudio().catch(() => undefined), { once: true })
      }

      if (isIOS()) {
        claimIosCallSession()
        if (primedMicPromise) {
          primedMicStream = await primedMicPromise
          primedMicPromise = null
        }
        // A refused mic must not fail the call. The room is already connected
        // by now, so throwing here drops the UI back to `idle` while the room
        // stays live: no mute/end controls, and the next tap only re-throws.
        // Mirror applyMute() and let micBlocked surface it instead.
        try {
          if (primedMicStream && !micMuted.value) {
            await publishPrimedMic(room, primedMicStream)
          } else if (!micMuted.value) {
            await enableMicrophone(room)
          }
        } catch {
          micStreamAllowed.value = false
        }
      } else {
        room.localParticipant.setAudioContext?.(audioContext())
        await applyMute(micMuted.value)
      }
    } catch (error) {
      // connect() may already have succeeded — anything failing after it, and
      // the room stays up while the UI falls back to `idle`. That strands a
      // live call with no controls and every retry tap re-throws the same
      // generic error, so tear the room down to match the state we show.
      clearTimers()
      if (roomRef.value && roomRef.value.state !== 'disconnected') {
        manualEnd.value = true
        try {
          await roomRef.value.disconnect()
        } catch {
          /* ignore */
        }
      }
      if (error?.status === 401 || error?.status === 403) {
        clearAuth(config.authTokenKey, config.authAgentKey)
        token.value = null
        loginStatus.value = 'pending'
        void issueToken(agentId.value)
      }
      if (error?.status === 429) {
        coolingDown.value = true
        cooldownTimer = setTimeout(() => {
          coolingDown.value = false
        }, RATE_LIMIT_COOLDOWN_MS)
      }
      showToast(startFailureMessage(error, copy.value))
      stopRinging()
      sequence.value = 'idle'
    } finally {
      starting.value = false
    }
  }

  const startCall = () => {
    if (coolingDown.value || config.pbBuilder) return
    // WebKit (Safari and Chrome on iOS) rejects getUserMedia while the
    // page is in a playback-only audio session. Claim play-and-record and
    // open the mic in this tap — after connect() is too late on iOS 17+.
    if (isIOS()) {
      claimIosCallSession()
      primedMicPromise = navigator.mediaDevices
        .getUserMedia({ audio: micCaptureOptions() })
        .then((stream) => {
          micStreamAllowed.value = true
          return stream
        })
        .catch(() => {
          micStreamAllowed.value = false
          showToast(copy.value.micAllowToast)
          return null
        })
    }
    unlockAudio()
    resumeOrbAudio()
    void roomRef.value?.startAudio?.().catch(() => undefined)
    playTapSound()
    callChannel?.postMessage({ type: 'call-started' })
    log.value = [
      {
        role: 'system',
        message: copy.value.connectingTo(scenario.value),
        timestamp: Date.now()
      }
    ]
    void beginCall()
  }

  const endCall = async () => {
    const nonce = sessionNonce.value
    manualEnd.value = true
    stopRinging()
    clearTimers()
    log.value = []
    try {
      await roomRef.value?.disconnect()
    } catch {
      /* ignore */
    }
    detachAudio()
    playCallEndSound()
    sequence.value = 'disconnected'
    micMuted.value = false
    setIdleSoon(nonce)
  }

  const toggleMute = async () => {
    if (muteBusy || !connected.value) return
    muteBusy = true
    playMuteSound(!micMuted.value)
    micMuted.value = !micMuted.value
    try {
      await applyMute(micMuted.value)
    } finally {
      muteBusy = false
    }
  }

  const onOtherTab = (event) => {
    if (event.data?.type !== 'call-started' || !inCall.value) return
    showToast(copy.value.otherTab)
    void endCall()
  }

  const onVisibility = () => {
    if (!connected.value) return
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      const nonce = sessionNonce.value
      hiddenTimer = setTimeout(() => {
        if (nonce !== sessionNonce.value) return
        showToast(copy.value.hiddenEnd)
        void endCall()
      }, HIDDEN_GRACE_MS)
    } else {
      resumeOrbAudio()
      unlockAudio()
      if (hiddenTimer) {
        clearTimeout(hiddenTimer)
        hiddenTimer = null
        if (hiddenAt && Date.now() - hiddenAt > HIDDEN_GRACE_MS) {
          showToast(copy.value.hiddenEnd)
          void endCall()
        }
        hiddenAt = 0
      }
      const room = roomRef.value
      if (room?.state === 'connected') {
        // Delay so a permission-sheet visibility flicker does not tear down
        // the mic we just published on connect.
        window.setTimeout(() => {
          if (room.state !== 'connected') return
          if (connectStartedAt && Date.now() - connectStartedAt < 2000) return
          void recoverCallAudio(room, micMuted.value)
        }, 400)
      }
    }
  }

  const recoverCallAudio = async (room, muted) => {
    if (room.state !== 'connected') return
    try {
      await disableMicrophone(room)
      if (!muted) await enableMicrophone(room)
    } catch {
      // A failed re-publish left the mic unpublished with micStreamAllowed
      // still true: the call looks healthy but the agent hears nothing for the
      // rest of it. Report it the way applyMute does so micBlocked shows the
      // hint and the button becomes a permission retry.
      micStreamAllowed.value = false
    }
    if (!room.canPlaybackAudio) {
      try {
        await room.startAudio()
      } catch {
        window.addEventListener('pointerdown', () => room.startAudio().catch(() => {}), { once: true })
      }
    }
    audioContexts().forEach((ctx) => ctx.state === 'suspended' && ctx.resume())
    attachedEls.forEach((el) => void el.play().catch(() => undefined))
  }

  const onPageHide = (event) => {
    // iOS fires pagehide for the mic permission sheet and bfcache. Killing
    // the room there drops a call that just connected.
    if (event.persisted || inCall.value) return
    void roomRef.value?.disconnect()
  }

  const onEscape = (event) => {
    if (event.key === 'Escape' && inCall.value) void endCall()
  }

  watch(agentId, (id) => {
    pendingStart.value = false
    clearAuth(config.authTokenKey, config.authAgentKey)
    token.value = null
    void issueToken(id)
  })

  watch(micBlocked, (blocked, wasBlocked) => {
    if (blocked) showToast(copy.value.micAllowToast)
    if (wasBlocked && !blocked && connected.value) {
      log.value = [
        ...log.value,
        { role: 'system', message: copy.value.micLive, timestamp: Date.now() }
      ]
    }
  })

  onMounted(() => {
    callChannel?.addEventListener('message', onOtherTab)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('keydown', onEscape)

    navigator.permissions
      ?.query({ name: 'microphone' })
      .then((status) => {
        micPermission.value = status.state
        micStreamAllowed.value = status.state === 'granted'
        status.onchange = () => {
          micPermission.value = status.state
          micStreamAllowed.value = status.state === 'granted'
          if (status.state === 'granted' && micBlocked.value) void probeMic()
          if (status.state === 'granted' && roomRef.value?.state === 'connected' && !micMuted.value) {
            void enableMicrophone(roomRef.value).catch(() => undefined)
          }
        }
      })
      .catch(() => undefined)

    void loadLiveKit()
    void ensureRoom()
    void issueToken(agentId.value)
  })

  onBeforeUnmount(() => {
    callChannel?.removeEventListener('message', onOtherTab)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onPageHide)
    document.removeEventListener('keydown', onEscape)
    clearTimers()
    void roomRef.value?.disconnect()
    detachAudio()
    stopRinging()
  })

  onBeforeRouteLeave(() => {
    if (inCall.value) void endCall()
  })

  return reactive({
    sequence,
    inCall,
    connected,
    ended,
    starting,
    coolingDown,
    loginLocked,
    micMuted,
    micBlocked,
    micDenied,
    isSpeaking,
    reconnecting,
    elapsedSeconds,
    log: visibleLog,
    outputAnalyser,
    inputAnalyser,
    startCall,
    endCall,
    toggleMute,
    requestMic,
    pbBuilder: config.pbBuilder
  })
}
